import React, { useState } from "react";

// 2026-08-08 — Backend brief §3: fn_get_conversation_detail returns a third
// top-level key `call` — the full phone_call_sessions record for voice
// conversations (null for chat, and for voice conversations with no linked
// session). Render the useful fields grouped; skip whatever is null so thin
// records stay small. `costs` is populated only for platform_admin and is
// null for everyone else — the key is always present, so gate the section on
// costs !== null, never on a client-side role guess.

const fmtStr = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
const fmtBool = (v) => (v === true ? "Yes" : v === false ? "No" : null);
const fmtNum = (v, suffix = "") =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : `${v}${suffix}`;
const fmtMoney = (v) =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : `$${Number(v).toFixed(2)}`;
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const titleCase = (s) => String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-0.5 text-xs">
    <span className="text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
    <span className="text-right text-gray-900 dark:text-gray-100 font-medium break-words">{value}</span>
  </div>
);

const VoiceCallDetails = ({ call }) => {
  const [open, setOpen] = useState(false);
  if (!call) return null;

  const groups = [
    {
      title: "Outcome",
      rows: [
        ["Outcome", fmtStr(call.outcome)],
        ["Resolution", fmtStr(call.resolution_status)],
        ["Lead captured", fmtBool(call.lead_captured)],
        ["Reservation made", fmtBool(call.reservation_made)],
        ["Reservation value", fmtMoney(call.reservation_value)],
        ["Base total", fmtMoney(call.base_total)],
        ["Nightly rate", fmtMoney(call.nightly_rate)],
        ["Site #", fmtStr(call.site_number)],
      ],
    },
    {
      title: "Caller",
      rows: [
        ["Phone", fmtStr(call.caller_phone)],
        ["Name", fmtStr(call.caller_name)],
        ["Email", fmtStr(call.caller_email)],
        ["Repeat caller", fmtBool(call.is_repeat_caller)],
        ["Previous calls", fmtNum(call.previous_call_count)],
        ["Region", fmtStr(call.caller_region)],
      ],
    },
    {
      title: "Intent",
      rows: [
        ["Inquiry type", fmtStr(call.inquiry_type)],
        ["Check-in", fmtDate(call.check_in_date_mentioned)],
        ["Check-out", fmtDate(call.check_out_date_mentioned)],
        ["Nights", fmtNum(call.num_nights_mentioned)],
        ["Party size", fmtNum(call.party_size_mentioned)],
        ["Pets", fmtNum(call.num_pets_mentioned)],
        ["RV length", call.rv_length_mentioned ? `${call.rv_length_mentioned} ft` : null],
        ["RV type", fmtStr(call.rv_type_mentioned)],
      ],
    },
    {
      title: "Handling",
      rows: [
        ["Transferred to human", fmtBool(call.transferred_to_human)],
        ["Transfer reason", fmtStr(call.transfer_reason)],
        ["Ended by", fmtStr(call.ended_by)],
        ["Voicemail left", fmtBool(call.voicemail_left)],
        ["Callback requested", fmtBool(call.callback_requested)],
        ["Callback number", fmtStr(call.callback_number)],
      ],
    },
    {
      title: "Quality",
      rows: [
        ["Sentiment", fmtStr(call.sentiment)],
        ["Call quality", fmtNum(call.call_quality_score)],
        ["Caller satisfaction", fmtNum(call.caller_satisfaction_rating)],
        ["Avg latency", call.avg_latency_ms != null ? `${call.avg_latency_ms} ms` : null],
        ["Complaint", fmtBool(call.contains_complaint)],
      ],
    },
  ]
    .map((g) => ({ ...g, rows: g.rows.filter(([, v]) => v !== null && v !== undefined) }))
    .filter((g) => g.rows.length > 0);

  const topics = Array.isArray(call.topics_detected) ? call.topics_detected : [];
  const questions = Array.isArray(call.key_questions_asked) ? call.key_questions_asked : [];
  const actions = Array.isArray(call.action_items) ? call.action_items : [];
  const followup = fmtStr(call.suggested_followup);
  const summary = fmtStr(call.summary);
  const hasAi = !!(summary || topics.length || questions.length || actions.length || followup);
  const recordingUrl = fmtStr(call.recording_url);
  // platform_admin only — everyone else gets null. Gate on null, not role.
  const costs = call.costs && typeof call.costs === "object" && !Array.isArray(call.costs) ? call.costs : null;

  if (!groups.length && !hasAi && !recordingUrl && !costs) return null;

  return (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/70 dark:bg-indigo-950/30 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-indigo-100/60 dark:hover:bg-indigo-900/30 transition-colors"
      >
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Voice call record
        </span>
        <svg
          className={`w-4 h-4 text-indigo-500 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3 border-t border-indigo-200/70 dark:border-indigo-800/60 pt-2">
          {recordingUrl && (
            <div>
              <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Recording</p>
              <audio controls preload="none" src={recordingUrl} className="w-full h-8" />
            </div>
          )}

          {groups.map((g) => (
            <div key={g.title}>
              <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">{g.title}</p>
              {g.rows.map(([label, value]) => (
                <Row key={label} label={label} value={value} />
              ))}
            </div>
          ))}

          {hasAi && (
            <div>
              <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">AI summary</p>
              {summary && <p className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{summary}</p>}
              {topics.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {topics.map((t, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-[10px] font-medium">
                      {titleCase(t)}
                    </span>
                  ))}
                </div>
              )}
              {questions.length > 0 && (
                <ul className="mt-1.5 space-y-0.5 text-xs text-gray-700 dark:text-gray-300 list-disc list-inside">
                  {questions.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              )}
              {actions.length > 0 && (
                <div className="mt-1.5">
                  <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">Action items</p>
                  <ul className="space-y-0.5 text-xs text-gray-700 dark:text-gray-300 list-disc list-inside">
                    {actions.map((a, i) => <li key={i}>{typeof a === "string" ? a : a?.action || a?.title || JSON.stringify(a)}</li>)}
                  </ul>
                </div>
              )}
              {followup && <p className="mt-1.5 text-xs text-gray-700 dark:text-gray-300"><span className="font-medium">Follow-up:</span> {followup}</p>}
            </div>
          )}

          {costs && (
            <div>
              <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">Costs</p>
              {Object.entries(costs)
                .filter(([, v]) => v !== null && v !== undefined)
                .map(([k, v]) => (
                  <Row
                    key={k}
                    label={titleCase(k)}
                    value={typeof v === "number" ? `$${v.toFixed(4)}` : String(v)}
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VoiceCallDetails;
